import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { Database, Search, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

function DbStatusCard({ dbStatus, onRefresh }) {
  if (!dbStatus) return null;
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2">
          <Database className="h-5 w-5" />
          Database Status
        </CardTitle>
        <Button variant="outline" size="sm" onClick={onRefresh}>
          <RefreshCw className="h-4 w-4 mr-1" />
          Opdater
        </Button>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-2xl font-bold">{dbStatus.counts?.bookings || 0}</p>
              <p className="text-sm text-slate-500">Bookings</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-2xl font-bold">{dbStatus.counts?.surveys || 0}</p>
              <p className="text-sm text-slate-500">Surveys</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-2xl font-bold">{dbStatus.counts?.coaching || 0}</p>
              <p className="text-sm text-slate-500">Coaching</p>
            </div>
            <div className="bg-slate-50 p-3 rounded-lg">
              <p className="text-2xl font-bold">{dbStatus.counts?.users || 0}</p>
              <p className="text-sm text-slate-500">Brugere</p>
            </div>
          </div>

          {dbStatus.last_hubhus_sync && (
            <div className="text-sm text-slate-500">
              Sidste Hubhus sync: {new Date(dbStatus.last_hubhus_sync.timestamp).toLocaleString('da-DK')}
            </div>
          )}

          <div>
            <h3 className="font-medium mb-2">Seneste bookings:</h3>
            <div className="space-y-1 text-sm max-h-48 overflow-y-auto">
              {(dbStatus.recent_bookings || []).map((b, i) => (
                <div key={i} className="flex justify-between bg-slate-50 p-2 rounded">
                  <span>{b.customer_name || 'Ukendt'}</span>
                  <span className="text-slate-500">{b.hubhus_booking_id}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function BookingDebugCard({ bookingDebug, bookingId, onRegenerate, regenerating }) {
  if (!bookingDebug) return null;
  
  if (bookingDebug.error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <p className="text-red-600">{bookingDebug.error}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-50 p-4 rounded-lg space-y-4">
      <div>
        <h4 className="font-medium">Booking</h4>
        {bookingDebug.booking ? (
          <div className="text-sm mt-1">
            <p>Kunde: {bookingDebug.booking.customer_name}</p>
            <p>Dato: {bookingDebug.booking.scheduled_start}</p>
            <p>Type: {bookingDebug.booking.booking_type}</p>
            <p>Konsulent ID: {bookingDebug.booking.consultant_user_id || 'Ikke tildelt'}</p>
          </div>
        ) : (
          <p className="text-red-600">IKKE FUNDET</p>
        )}
      </div>

      <div>
        <h4 className="font-medium">Survey</h4>
        {bookingDebug.survey ? (
          <div className="text-sm mt-1">
            <p>Besvaret: {bookingDebug.survey.submitted_at || 'Nej'}</p>
            <p>Har svar: {bookingDebug.survey.has_answers ? 'Ja' : 'Nej'}</p>
            <p>Har signaler: {bookingDebug.survey.has_signals ? 'Ja' : 'Nej'}</p>
          </div>
        ) : (
          <p className="text-slate-500">Ingen survey</p>
        )}
      </div>

      <div>
        <h4 className="font-medium">Coaching records ({(bookingDebug.coaching_records || []).length})</h4>
        <div className="space-y-2 mt-1">
          {(bookingDebug.coaching_records || []).map((c, i) => (
            <div key={i} className="text-sm bg-white p-2 rounded border">
              <p>Source: <Badge>{c.source}</Badge></p>
              <p>Tips: {c.tips_count}</p>
              <p>Genereret: {c.generated_at}</p>
            </div>
          ))}
        </div>
        
        {bookingDebug.booking && (
          <Button 
            size="sm" 
            className="mt-2"
            onClick={() => onRegenerate(bookingId)}
            disabled={regenerating}
          >
            {regenerating ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
            Regenerer coaching
          </Button>
        )}
      </div>

      <div>
        <h4 className="font-medium">Short links ({(bookingDebug.short_links || []).length})</h4>
        <div className="text-sm mt-1 space-y-1">
          {(bookingDebug.short_links || []).map((l, i) => (
            <p key={i}>/p/{l.code} - Expires: {l.expires_at}</p>
          ))}
          {(bookingDebug.short_links || []).length === 0 && (
            <p className="text-slate-500">Ingen SMS links</p>
          )}
        </div>
      </div>
    </div>
  );
}

const DebugPage = () => {
  const [dbStatus, setDbStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [bookingId, setBookingId] = useState('');
  const [bookingDebug, setBookingDebug] = useState(null);
  const [loadingBooking, setLoadingBooking] = useState(false);
  const [regenerating, setRegenerating] = useState(false);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('auth_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const loadDbStatus = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/debug/database-status`, {
        headers: getAuthHeaders()
      });
      setDbStatus(res.data);
    } catch (err) {
      console.error('Failed to load db status:', err);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    loadDbStatus();
  }, [loadDbStatus]);

  const searchBookings = async () => {
    if (!searchTerm) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/api/admin/debug/search?customer_name=${encodeURIComponent(searchTerm)}`, {
        headers: getAuthHeaders()
      });
      setSearchResults(res.data);
    } catch (err) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  };

  const debugBooking = async () => {
    if (!bookingId) return;
    setLoadingBooking(true);
    setBookingDebug(null);
    try {
      const res = await axios.get(`${API}/api/admin/debug/booking/${bookingId}`, {
        headers: getAuthHeaders()
      });
      setBookingDebug(res.data);
    } catch (err) {
      setBookingDebug({ error: err.response?.data?.detail || 'Booking ikke fundet' });
    } finally {
      setLoadingBooking(false);
    }
  };

  const regenerateCoaching = async (bid) => {
    setRegenerating(true);
    try {
      const res = await axios.post(`${API}/api/admin/regenerate-coaching/${bid}`, {}, {
        headers: getAuthHeaders()
      });
      alert('Coaching regenereret! Source: ' + res.data.source + ', Tips: ' + res.data.tips_count);
      debugBooking();
    } catch (err) {
      alert('Fejl: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegenerating(false);
    }
  };

  const runHistoricalSync = async () => {
    if (!window.confirm('Dette vil køre en fuld Magento sync for alle bookings siden første survey. Dette kan tage flere minutter. Fortsæt?')) {
      return;
    }
    try {
      const res = await axios.post(`${API}/api/admin/magento/full-historical-sync`, {}, {
        headers: getAuthHeaders()
      });
      alert(res.data.message);
    } catch (err) {
      alert('Fejl: ' + (err.response?.data?.detail || err.message));
    }
  };

  const runMonteringOrderSync = async () => {
    try {
      const res = await axios.post(`${API}/api/admin/magento/sync-montering`, {}, {
        headers: getAuthHeaders()
      });
      alert(res.data.message);
    } catch (err) {
      alert('Fejl: ' + (err.response?.data?.detail || err.message));
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-slate-900 font-outfit">Debug</h1>
        <p className="text-slate-500 mt-1">Undersøg database og booking data</p>
      </div>

      <DbStatusCard dbStatus={dbStatus} onRefresh={loadDbStatus} />

      {/* Magento Historical Sync */}
      <Card>
        <CardHeader>
          <CardTitle>Magento Historisk Sync</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Kør en fuld sync af konverteringsdata fra Magento for alle bookings siden første survey blev sendt. 
            Dette er en engangskørsel der kan tage flere minutter.
          </p>
          <Button onClick={runHistoricalSync}>
            Kør fuld historisk sync
          </Button>
        </CardContent>
      </Card>

      {/* Magento Montering Order Sync */}
      <Card>
        <CardHeader>
          <CardTitle>Montering Ordre-Links</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            Synkroniser ordre-links fra Magento til montering-aftaler. Dette tilføjer direkte links til Magento-ordrer
            på monteringsaftaler i kalenderen, så konsulenter hurtigt kan se ordredetaljer.
          </p>
          <Button onClick={runMonteringOrderSync} data-testid="sync-montering-btn">
            Synkroniser montering-ordrer
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Søg booking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Søg på kundenavn..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && searchBookings()}
            />
            <Button onClick={searchBookings} disabled={searching}>
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Søg'}
            </Button>
          </div>

          {searchResults && (
            <div>
              <p className="text-sm text-slate-500 mb-2">Fandt {searchResults.count} resultater</p>
              <div className="space-y-2">
                {(searchResults.bookings || []).map((b, i) => (
                  <div key={i} className="flex justify-between items-center bg-slate-50 p-3 rounded">
                    <div>
                      <p className="font-medium">{b.customer_name}</p>
                      <p className="text-sm text-slate-500">ID: {b.hubhus_booking_id}</p>
                    </div>
                    <Button size="sm" variant="outline" onClick={() => {
                      setBookingId(b.hubhus_booking_id);
                    }}>
                      Vælg
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5" />
            Debug specifik booking
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Booking ID (fra Hubhus)..."
              value={bookingId}
              onChange={(e) => setBookingId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && debugBooking()}
            />
            <Button onClick={debugBooking} disabled={loadingBooking}>
              {loadingBooking ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Debug'}
            </Button>
          </div>

          <BookingDebugCard 
            bookingDebug={bookingDebug} 
            bookingId={bookingId}
            onRegenerate={regenerateCoaching}
            regenerating={regenerating}
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default DebugPage;
